import {
  validationCapabilities,
  type Field,
  type ValidationMessageKey,
} from "@proof/core";
import { Select, Control } from "./components";
import { HelpTip } from "./HelpTip";

export const validationRuleLabels: Record<string, string> = {
  none: "No additional rule",
  regex: "Custom Regex",
  url: "Website URL (HTTP / HTTPS)",
  email: "Email address",
  phone: "Phone number",
  integer: "Whole number",
  number: "Decimal number",
  positive: "Positive number",
  nonnegative: "Zero or positive number",
  "postal-us": "US ZIP / ZIP+4",
  "postal-ca": "Canadian postal code",
  uuid: "UUID",
  "hex-color": "Hex color",
  alphanumeric: "Letters and numbers",
};
const messageLabels: Record<ValidationMessageKey, string> = {
  required: "Required value message",
  minLength: "Too few characters message",
  maxLength: "Too many characters message",
  min: "Below minimum message",
  max: "Above maximum message",
  pattern: "Custom Regex message",
  rule: "Validation rule message",
  minSelected: "Too few selections message",
  maxSelected: "Too many selections message",
};
const numberInput = (
  value: unknown,
  onChange: (value: number | undefined) => void,
  min?: number,
) => (
  <input
    type="number"
    inputMode="numeric"
    className="numeric"
    min={min}
    value={typeof value === "number" ? value : ""}
    onChange={(event) =>
      onChange(
        event.target.value === "" ? undefined : Number(event.target.value),
      )
    }
  />
);
/**
 * Validation controls for one control type. Only rules and limits that apply
 * to that type are offered; the engine ignores anything else.
 */
export function ValidationSettingsFields({
  type,
  draft,
  set,
}: {
  type: Field["type"];
  draft: Record<string, any>;
  set(key: string, value: unknown): void;
}) {
  const can = validationCapabilities(type);
  const rule: string = can.rules.includes(draft.rule) ? draft.rule : "none";
  const regex = rule === "regex" || !!draft.pattern;
  const messageKeys: ValidationMessageKey[] = [
    "required",
    ...(can.characters ? (["minLength", "maxLength"] as const) : []),
    ...(can.range ? (["min", "max"] as const) : []),
    ...(can.selections ? (["minSelected", "maxSelected"] as const) : []),
    ...(regex && can.characters ? (["pattern"] as const) : []),
    ...(!["none", "regex"].includes(rule) ? (["rule"] as const) : []),
  ];
  const setMessage = (key: ValidationMessageKey, value: string) => {
    const messages = { ...(draft.messages || {}) };
    if (value) messages[key] = value;
    else delete messages[key];
    set("messages", Object.keys(messages).length ? messages : undefined);
  };
  return (
    <>
      {can.rules.length > 1 && (
        <Control
          label="Validation rule"
          help="Rules offered depend on the control type. Choose Custom Regex to check the value against your own regular expression."
          helpDocs="/help?guide=field-policies"
        >
          <Select value={rule} onChange={(e) => set("rule", e.target.value)}>
            {can.rules.map((value) => (
              <option key={value} value={value}>
                {validationRuleLabels[value] || value}
              </option>
            ))}
          </Select>
        </Control>
      )}
      {can.characters && (
        <div className="control-grid">
          <Control label="Minimum characters">
            {numberInput(draft.minLength, (v) => set("minLength", v), 0)}
          </Control>
          <Control label="Maximum characters">
            {numberInput(draft.maxLength, (v) => set("maxLength", v), 0)}
          </Control>
        </div>
      )}
      {can.range && (
        <div className="control-grid">
          <Control label="Minimum value">
            {numberInput(draft.min, (v) => set("min", v))}
          </Control>
          <Control label="Maximum value">
            {numberInput(draft.max, (v) => set("max", v))}
          </Control>
        </div>
      )}
      {can.selections && (
        <div className="control-grid">
          <Control
            label="Minimum selections"
            note="Applies to checkbox groups. Leave blank for no minimum."
          >
            {numberInput(draft.minSelected, (v) => set("minSelected", v), 0)}
          </Control>
          <Control label="Maximum selections">
            {numberInput(draft.maxSelected, (v) => set("maxSelected", v), 0)}
          </Control>
        </div>
      )}
      {regex && can.characters && (
        <div className="control-grid">
          <Control
            label="Regular expression"
            note="Unicode regular expression. Numbered capture groups ( … ) are supported; lookarounds, back-references and nested repeats are rejected."
          >
            <input
              value={draft.pattern || ""}
              spellCheck={false}
              onChange={(e) => set("pattern", e.target.value || undefined)}
            />
          </Control>
          <Control label="Regex behaviour">
            <Select
              value={draft.patternMode || "match"}
              onChange={(e) =>
                set(
                  "patternMode",
                  e.target.value === "match" ? undefined : e.target.value,
                )
              }
            >
              <option value="match">Value must match</option>
              <option value="reject">Reject values containing a match</option>
            </Select>
          </Control>
        </div>
      )}
      <details className="validation-messages">
        <summary>
          Custom messages
          <HelpTip
            title="Custom validation messages"
            summary="Replace the default wording shown when a rule fails."
            docs="/help?guide=field-policies"
          >
            <p>
              Leave a message blank to use the default wording. Messages can
              reference the entered value and, for a Custom Regex that rejects
              matching values, its capture groups:
            </p>
            <ul>
              <li>
                <code>$0</code> — the value the person entered
              </li>
              <li>
                <code>$1</code>…<code>$9</code> — the text matched by that
                capture group (Reject values containing a match only)
              </li>
              <li>
                <code>$$</code> — a literal dollar sign
              </li>
            </ul>
            <p>
              Example: expression <code>(\d{"{3}"})-(\d{"{4}"})</code>, behaviour
              Reject, message <code>Remove the phone number $1-$2</code>.
              References to groups that do not exist block Apply and Save.
            </p>
          </HelpTip>
        </summary>
        {messageKeys.map((key) => (
          <Control key={key} label={messageLabels[key]}>
            <input
              value={draft.messages?.[key] || ""}
              placeholder="Default message"
              onChange={(e) => setMessage(key, e.target.value)}
            />
          </Control>
        ))}
      </details>
    </>
  );
}
